import { FC, PropsWithChildren, ReactNode } from 'react'

import _ from 'lodash'
import { useParams } from 'react-router-dom'
import { styled } from 'styled-components'

import { PendingEventHandler } from 'core/components/lib/Button/Button'
import CardSection from 'core/components/lib/CardSection/CardSection'
import Clickable from 'core/components/lib/Clickable'
import { ClickableProps } from 'core/components/lib/Clickable/Clickable'
import ConfirmDialog from 'core/components/lib/ConfirmDialog'
import PermissionDeniedDialog from 'core/components/lib/PermissionDeniedDialog/PermissionDeniedDialog'
import Spinner from 'core/components/lib/Spinner'
import { useHasPermission, useBorrowerUrl } from 'core/hooks'
import { Permission } from 'core/types'

import { useFormDisabled } from 'loan/components/actionCards/shared/DisabledContext'

type BackButtonProps = PropsWithChildren<{
  variant?: ClickableProps['variant']
}>

/** @deprecated Use CardSectionSubmit that is not in the legacy folder */
export const BackButton: FC<BackButtonProps> = ({ children = 'Back', variant = 'ghost' }) => {
  const { loanId } = useParams<{ loanId: string }>()
  const url = useBorrowerUrl(loanId ? `/loans/${loanId}/activity` : '/loans')

  return (
    <Clickable to={url} variant={variant}>
      {children}
    </Clickable>
  )
}

/** @deprecated Use CardSectionSubmit that is not in the legacy folder */
export type SubmitButtonProps = {
  disabled: boolean
  loading: boolean
  success?: boolean
  submitText: string
  onSubmit: PendingEventHandler
  permissions?: Permission | Array<Permission>
  skipConfirm?: boolean
}

/** @deprecated Use CardSectionSubmit that is not in the legacy folder */
export const SubmitButton: FC<SubmitButtonProps> = ({
  disabled,
  loading,
  success = false,
  submitText,
  onSubmit,
  permissions,
  skipConfirm,
}) => {
  const isDisabled = useFormDisabled(disabled)

  const hasPermission = useHasPermission(permissions)

  const common = {
    children: loading ? 'Processing' : submitText,
    disabled: isDisabled || loading,
    variant: 'secondary',
  } as const

  return (
    <>
      {!hasPermission ?
        <PermissionDeniedDialog title={submitText}>
          <Clickable {...common} />
        </PermissionDeniedDialog>
      : success ?
        <BackButton variant='secondary'>Done</BackButton>
      : skipConfirm ?
        <Clickable {...common} onClick={onSubmit} />
      : <ConfirmDialog
          content={`Are you sure you want to ${submitText.toLowerCase()}?`}
          onConfirm={onSubmit}
          title={submitText}
        >
          <Clickable {...common} />
        </ConfirmDialog>
      }
      {loading && <Spinner />}
    </>
  )
}

type CardSectionSubmitProps = {
  submitText?: string
  cancelText?: string
  onSubmit?: PendingEventHandler
  children?: ReactNode
  disabled?: boolean
  loading?: boolean
  success?: boolean
  skipConfirm?: boolean
}

/** @deprecated Use CardSectionSubmit that is not in the legacy folder */
const CardSectionSubmit: FC<CardSectionSubmitProps> = ({
  submitText,
  cancelText,
  onSubmit,
  children,
  disabled = false,
  loading = false,
  success = false,
  skipConfirm,
}) => (
  <CardSection>
    <SubmitButtonsContainer>
      <BackButton variant='ghost'>{cancelText}</BackButton>

      {children ?? (
        <SubmitButton
          disabled={disabled}
          loading={loading}
          onSubmit={onSubmit ?? _.noop}
          skipConfirm={skipConfirm}
          submitText={submitText || 'Submit'}
          success={success}
        />
      )}
    </SubmitButtonsContainer>
  </CardSection>
)

export default CardSectionSubmit

/** @deprecated Use CardSectionSubmit that is not in the legacy folder */
export const SubmitButtonsContainer = styled.div<{ margin?: string }>`
  display: flex;
  flex-direction: row;
  gap: 32px;
  align-items: center;
  justify-content: flex-end;
  margin: ${(p) => p.margin ?? '16px 0 0 0'};
`
